import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Key,
  Edit,
  Save,
  X,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ClientProfile = () => {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    identification: user?.identification || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const { data: profileStats, isLoading } = useQuery(
    ['client-profile-stats', user?.id],
    () => api.get(`/profile/client/${user?.id}/stats`).then(res => res.data),
    {
      enabled: !!user?.id,
      staleTime: 30000
    }
  );

  const updateProfileMutation = useMutation(
    (data) => api.put(`/profile/client/${user?.id}`, data),
    {
      onSuccess: (response) => {
        updateUser(response.data.user);
        setEditMode(false);
        queryClient.invalidateQueries(['client-profile-stats']);
      }
    }
  );

  const changePasswordMutation = useMutation(
    (data) => api.put(`/profile/client/${user?.id}/password`, data),
    {
      onSuccess: () => {
        setShowPasswordForm(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    }
  );

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definido';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileData);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    changePasswordMutation.mutate(passwordData);
  };

  const handleEditCancel = () => {
    setEditMode(false);
    setProfileData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      identification: user?.identification || ''
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  const stats = profileStats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            👤 Mi Perfil
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestiona tu información personal y configuración
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                  <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Información Personal
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Datos básicos de tu cuenta
                  </p>
                </div>
              </div>

              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="btn-secondary"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEditCancel}
                    className="btn-secondary"
                    disabled={updateProfileMutation.isLoading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleProfileSubmit}
                    className="btn-primary"
                    disabled={updateProfileMutation.isLoading}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </button>
                </div>
              )}
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Nombre completo</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                      className="input"
                      required
                    />
                  ) : (
                    <div className="input bg-gray-50 dark:bg-dark-800">
                      {user?.fullName}
                    </div>
                  )}
                </div>

                <div>
                  <label className="label">Correo electrónico</label>
                  {editMode ? (
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="input"
                      required
                    />
                  ) : (
                    <div className="input bg-gray-50 dark:bg-dark-800">
                      {user?.email}
                    </div>
                  )}
                </div>

                <div>
                  <label className="label">Teléfono</label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="input"
                    />
                  ) : (
                    <div className="input bg-gray-50 dark:bg-dark-800">
                      {user?.phone || 'No definido'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="label">Identificación</label>
                  <div className="input bg-gray-50 dark:bg-dark-800">
                    {user?.identification}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-dark-700">
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Miembro desde: {formatDate(user?.createdAt)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span>Rol: Cliente</span>
                  </div>
                </div>
              </div>
            </form>

            {updateProfileMutation.error && (
              <div className="mt-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                  <span className="text-sm text-danger-700 dark:text-danger-300">
                    {updateProfileMutation.error.response?.data?.error || 'Error al actualizar perfil'}
                  </span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Security Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-warning-100 dark:bg-warning-900/30 rounded-xl">
                  <Key className="w-6 h-6 text-warning-600 dark:text-warning-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Seguridad
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Gestiona tu contraseña y configuración de seguridad
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="btn-secondary"
              >
                <Key className="w-4 h-4 mr-2" />
                Cambiar Contraseña
              </button>
            </div>

            {showPasswordForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handlePasswordSubmit}
                className="space-y-4 pt-4 border-t border-gray-200 dark:border-dark-700"
              >
                <div>
                  <label className="label">Contraseña actual</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="input pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label">Nueva contraseña</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="input pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="label">Confirmar nueva contraseña</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="input pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={changePasswordMutation.isLoading}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Cambiar Contraseña
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPasswordForm(false)}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>

                {changePasswordMutation.error && (
                  <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                      <span className="text-sm text-danger-700 dark:text-danger-300">
                        {changePasswordMutation.error.response?.data?.error || 'Error al cambiar contraseña'}
                      </span>
                    </div>
                  </div>
                )}

                {changePasswordMutation.isSuccess && (
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                      <span className="text-sm text-success-700 dark:text-success-300">
                        Contraseña cambiada exitosamente
                      </span>
                    </div>
                  </div>
                )}
              </motion.form>
            )}
          </motion.div>
        </div>

        {/* Profile Stats Sidebar */}
        <div className="space-y-6">
          {/* Account Status */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Estado de la Cuenta
            </h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Verificación</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                  <span className="text-sm font-medium text-success-600 dark:text-success-400">Verificado</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Términos y Condiciones</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                  <span className="text-sm font-medium text-success-600 dark:text-success-400">Aceptados</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Último acceso</span>
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(user?.lastLogin)}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;